import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import classNames from 'classnames';
import { useStoreState } from 'easy-peasy';
import * as R from 'ramda';
import { useToggle, useClickAway, useMeasure } from 'react-use';
import { Authorised } from 'Components/Authorised';
import {
  usePartners,
  useCustomers,
  useParticipants,
  useWarehouses,
  useConsolidation,
  useDeliveries,
  CustomerRequestController,
} from '~/store/models';
import { Tooltip } from 'Components/nui';
import { getEnv } from '~/utils';
import { TendersLink } from './Tenders';
import { useLocale } from '~/lang';
import { toast } from 'react-toastify';
import { FormattedMessage, useIntl } from 'react-intl';
import { Popover } from 'antd';
import Icon from 'Components/Icon';
import { useMarketOrders } from './Market/hooks/useMarketOrders';

const MobileMenuLink = ({ exact, to, title, iconClass }) => (
  <li>
    <NavLink
      exact={exact}
      to={to}
      activeClassName="selected"
      className="box"
      title={title}
    >
      <span className={iconClass}>{title}</span>
    </NavLink>
  </li>
);

const MobileMenu = () => {
  const { formatMessage } = useIntl();
  const { marketplace, tender } = useStoreState(
    state => state.auth.solutionSettings
  );
  return (
    <div className="mbar mobile-nav">
      <ul>
        {marketplace && (
          <MobileMenuLink
            exact
            to="/"
            title={formatMessage({
              id: 'navbar-tab-marketplace',
              defaultMessage: 'Marketplace',
            })}
            iconClass="icon-bank"
          />
        )}
        {tender && (
          <MobileMenuLink
            to="/tenders"
            title={formatMessage({
              id: 'navbar-tab-tenders',
              defaultMessage: 'Tenders',
            })}
            iconClass="icon-hammer"
          />
        )}
        <MobileMenuLink
          to="/orders"
          title={formatMessage({
            id: 'navbar-tab-orders',
            defaultMessage: 'Orders',
          })}
          iconClass="icon-doc-text-inv"
        />
        <MobileMenuLink
          to="/trades"
          title={formatMessage({
            id: 'navbar-tab-trades',
            defaultMessage: 'Trades',
          })}
          iconClass="icon-exchange"
        />
      </ul>
    </div>
  );
};

const SecondaryMenu = ({ smallMainNav, canCreateLiquidity }) => {
  const { formatMessage } = useIntl();
  const participants = useParticipants();
  const customers = useCustomers();
  const warehouses = useWarehouses();
  const partners = usePartners();
  const consolidation = useConsolidation();
  const deliveries = useDeliveries();
  const isSolutionOwner = useStoreState(state => state.auth.isSolutionOwner);
  const isdao = useStoreState(state => state.auth.isdao);
  const solution = useStoreState(state => state.auth.solution);
  const routeTypes = ['port', 'zone'];
  const hasRoutes =
    isSolutionOwner &&
    R.any(R.includes(R.__, routeTypes), [
      ...solution.freight_types,
      solution.freight,
    ]);

  const ref = useRef(null);
  const [open, toggle] = useToggle(false);
  useClickAway(ref, () => {
    toggle(false);
  });

  const SecondaryMenuLink = ({ to, title, tooltip }) => (
    <li>
      <NavLink
        to={to}
        activeClassName="selected"
        title={tooltip ?? title}
        onClick={toggle}
      >
        {title}
      </NavLink>
    </li>
  );

  return (
    <div ref={ref} className="secondary-holder">
      <Tooltip
        title={
          <span className="icon-tooltip">
            <span className="icon-info-circled"></span>
            <FormattedMessage
              id="navbar-tab-view-pages"
              description="View pages ellipsis option in navbar"
              defaultMessage="View pages"
            />
          </span>
        }
      >
        <button className="icon-ellipsis show-more" onClick={toggle} />
      </Tooltip>
      {open && (
        <nav className={classNames('secondary', 'open')}>
          <ul>
            {smallMainNav && (
              <>
                {canCreateLiquidity && (
                  <SecondaryMenuLink
                    to="/lp-overview"
                    title={formatMessage({
                      id: 'navbar-tab-lp-overview',
                      defaultMessage: 'LP Overview',
                    })}
                  />
                )}
                <SecondaryMenuLink
                  to="/orders"
                  title={formatMessage({
                    id: 'navbar-tab-orders',
                    defaultMessage: 'Orders',
                  })}
                />
                <SecondaryMenuLink
                  to="/trades"
                  title={formatMessage({
                    id: 'navbar-tab-trades',
                    defaultMessage: 'Trades',
                  })}
                />
                <Authorised to="view" model={participants}>
                  <SecondaryMenuLink
                    to="/participants"
                    title={formatMessage({
                      id: 'navbar-tab-participants',
                      defaultMessage: 'Participants',
                    })}
                  />
                </Authorised>
                <Authorised to="view" model={consolidation}>
                  <SecondaryMenuLink
                    to="/consolidation"
                    title={formatMessage({
                      id: 'navbar-tab-consolidation',
                      defaultMessage: 'Consolidation',
                    })}
                  />
                </Authorised>
                <Authorised to="view" model={deliveries}>
                  <SecondaryMenuLink
                    to="/deliveries"
                    title={formatMessage({
                      id: 'navbar-tab-deliveries',
                      defaultMessage: 'Deliveries',
                    })}
                  />
                </Authorised>
              </>
            )}
            {hasRoutes && (
              <SecondaryMenuLink
                to="/routes"
                title={formatMessage({
                  id: 'navbar-tab-routes',
                  defaultMessage: 'Routes',
                })}
              />
            )}
            <Authorised to="view" model={partners}>
              <SecondaryMenuLink
                to="/partners"
                title={formatMessage({
                  id: 'navbar-tab-partners',
                  defaultMessage: 'Partners',
                })}
              />
            </Authorised>

            <SecondaryMenuLink
              to="/products"
              title={formatMessage({
                id: 'navbar-tab-products',
                defaultMessage: 'Products',
              })}
            />
            <Authorised to="view" model={warehouses}>
              <SecondaryMenuLink
                to="/locations"
                title={formatMessage({
                  id: 'navbar-tab-locations',
                  defaultMessage: 'Locations',
                })}
              />
            </Authorised>
            <Authorised to="view" model={customers}>
              <SecondaryMenuLink
                to="/customers"
                title={formatMessage({
                  id: 'navbar-tab-customers',
                  defaultMessage: 'Customers',
                })}
              />
            </Authorised>
            <Authorised to="view" model={CustomerRequestController}>
              <SecondaryMenuLink
                to="/customer-requests"
                title={formatMessage({
                  id: 'navbar-tab-customer-request',
                  defaultMessage: 'Customer requests',
                })}
              />
            </Authorised>
            {(isSolutionOwner || isdao) && (
              <SecondaryMenuLink
                to="/reports"
                title={formatMessage({
                  id: 'navbar-tab-reports',
                  defaultMessage: 'Reports',
                })}
              />
            )}
            <SecondaryMenuLink
              to="/users"
              title={formatMessage({
                id: 'navbar-tab-users',
                defaultMessage: 'Users',
              })}
            />
          </ul>
        </nav>
      )}
    </div>
  );
};

const DashboardButton = () => {
  const solution = useStoreState(state => state.auth.solution);

  const dashboards = R.propOr([], 'dashboards', solution);

  if (dashboards.length === 0) return null;

  return (
    <Tooltip
      title={
        <span className="icon-tooltip">
          <span className="icon-info-circled" />
          <FormattedMessage
            id="navbar-tab-dashboard"
            description="Dashboard option in navbar"
            defaultMessage="Dashboards are configurable to aid your trade decision making process."
          />
        </span>
      }
    >
      <NavLink to="/dashboard">
        <Icon name="finance" />
      </NavLink>
    </Tooltip>
  );
};

const TradeButtons = () => {
  const solutionDivisions = useStoreState(
    state => state.auth.solutionDivisions
  );
  const restrictedAccess = solutionDivisions.some(s => s?.restrictedaccess);
  const tradeActions = useStoreState(state => state.auth.userTradeActions);
  const marketplace = useStoreState(
    state => state.auth.solutionSettings.marketplace
  );

  if (!marketplace || restrictedAccess) return <></>;

  return (
    <div className="bset button-set">
      {R.includes('buy', tradeActions) && (
        <Tooltip
          title={
            <span className="icon-tooltip">
              <span className="icon-info-circled"></span>
              <FormattedMessage
                id="navbar-button-placebid-tooltip"
                description="Tooltip for 'Place bid' button in Navbar"
                defaultMessage="Placing a bid means you are buying"
              />
            </span>
          }
        >
          <Link to="/orders/bid" role="button" className="button bid">
            <FormattedMessage
              id="navbar-button-placebid"
              description="Place bid button"
              defaultMessage="Place bid"
            />
          </Link>
        </Tooltip>
      )}
      {R.includes('sell', tradeActions) && (
        <Tooltip
          title={
            <span className="icon-tooltip">
              <span className="icon-info-circled" />
              <FormattedMessage
                id="navbar-button-placeoffer-tooltip"
                description="Tooltip for 'Place offer' button in Navbar"
                defaultMessage="Placing an offer means you are selling"
              />
            </span>
          }
        >
          <Link to="/orders/offer" role="button" className="button offer">
            <FormattedMessage
              id="navbar-button-placeoffer"
              description="Place offer button"
              defaultMessage="Place offer"
            />
          </Link>
        </Tooltip>
      )}
    </div>
  );
};

const Lang = () => {
  const ref = useRef();
  const [open, toggle] = useToggle(false);
  const locale = useLocale();

  useClickAway(ref, () => void toggle(false));

  const style = {
    backgroundColor: 'white',
  };

  const onClick = value => async () => {
    const { success } = await locale.change(value);

    if (success) window.location.reload();
    else {
      toast.error('Could not update language.');
    }
  };

  const isSelected = id => id === locale.current;

  return (
    <Popover
      content={
        <div className="lang-dropdown">
          <h4>
            <FormattedMessage
              id="navbar-language-picker-heading"
              description="Heading 'Change language' for Language picker"
              defaultMessage="Change language"
            />
          </h4>
          <ul>
            {locale.options.map(([id, { name }]) => (
              <li key={id}>
                <button
                  className="link-button"
                  disabled={isSelected(id)}
                  onClick={onClick(id)}
                >
                  {name}
                </button>
                {isSelected(id) && <Icon name="check" />}
              </li>
            ))}
          </ul>
        </div>
      }
      trigger="click"
      placement="bottomRight"
    >
      <button className="link-button lang-button">
        {locale.current.toUpperCase()}
        <Icon name="arrow_drop_down" />
      </button>
    </Popover>
  );
};

const HelpSection = forwardRef((_, ref) => {
  return (
    <div className="help-section config-links has-lang-switcher" ref={ref}>
      <div className="help-section-inner">
        <Tooltip
          title={
            <span className="icon-tooltip">
              <span className="icon-info-circled" />
              <FormattedMessage
                id="navbar-tab-portal"
                description="Portal option in navbar"
                defaultMessage="The Nui Portal, gives you a unique view of physical trade across our marketplaces, and oversight of upcoming tenders across all Nui platforms."
              />
            </span>
          }
        >
          <a
            href={
              import.meta.env.VITE_APP_PORTAL_URL ||
              'https://portal.nuimarkets.com'
            }
            target="_blank"
            rel="noreferrer"
            className="help-section-portal"
          >
            <Icon name="dashboard" />
          </a>
        </Tooltip>
        <DashboardButton />
        <div className="nav-separator"></div>
        <Tooltip
          title={
            <span className="icon-tooltip">
              <span className="icon-info-circled" />
              <FormattedMessage
                id="navbar-tab-settings"
                description="Settings option in navbar"
                defaultMessage="Settings"
              />
            </span>
          }
        >
          <Link to="/account/config/notifications">
            <Icon name="settings" />
          </Link>
        </Tooltip>
        <Lang />
      </div>
    </div>
  );
});

const MainNav = ({ smallMainNav, canCreateLiquidity }) => {
  const { formatMessage } = useIntl();
  const domain = getEnv(import.meta.env.VITE_APP_OLD_DOMAIN);
  const user = useStoreState(state => state.auth.userModel);
  const isadmin = R.equals(
    'ea5befd8-958c-466e-96b5-a8513a0c416e',
    R.path(['company', 'id'], user)
  );

  const { marketplace, tender } = useStoreState(
    state => state.auth.solutionSettings
  );
  const { data: marketData } = useMarketOrders();
  const totalOffers =
    marketData?.orders?.filter(x => x.type === 'offer').length || 0;
  const totalBids =
    marketData?.orders?.filter(x => x.type === 'bid').length || 0;
  const participants = useParticipants();
  const consolidation = useConsolidation();
  const deliveries = useDeliveries();

  return (
    <nav className="main">
      <ul>
        {marketplace && (
          <li className="marketplace-link">
            <NavLink
              to="/"
              activeClassName="selected"
              className="icon-bank"
              title={formatMessage({
                id: 'navbar-tab-marketplace',
                defaultMessage: 'Marketplace',
              })}
              isActive={(match, location) =>
                ['/', '/overview'].includes(location.pathname)
              }
            >
              <FormattedMessage
                id="navbar-tab-marketplace"
                description="Top level/navbar marketplace link"
                defaultMessage="Marketplace"
              />
            </NavLink>
            {(totalOffers > 0 || totalBids > 0) && (
              <span className="activity-status">
                {!!totalOffers && (
                  <>
                    <FormattedMessage
                      id="navbar-tab-marketplace-sub-offers"
                      description="Total {count} of offers"
                      defaultMessage="{count, plural, =1 {1 offer} other {# offers}}"
                      values={{ count: totalOffers }}
                    />
                    {!!totalBids && ' | '}
                  </>
                )}
                {!!totalBids && (
                  <FormattedMessage
                    id="navbar-tab-marketplace-sub-bids"
                    description="Total {count} of bids"
                    defaultMessage="{count, plural, =1 {1 bid} other {# bids}}"
                    values={{ count: totalBids }}
                  />
                )}
              </span>
            )}
          </li>
        )}

        {tender && <TendersLink />}

        {!smallMainNav && (
          <>
            {canCreateLiquidity && (
              <li className="navsmall">
                <NavLink
                  to="/lp-overview"
                  activeClassName="selected"
                  title={formatMessage({
                    id: 'navbar-tab-lp-overview',
                    defaultMessage: 'LP Overview',
                  })}
                >
                  <FormattedMessage
                    id="navbar-tab-lp-overview"
                    description="LP Overview option in navbar"
                    defaultMessage="LP Overview"
                  />
                </NavLink>
              </li>
            )}
            <li className="navsmall">
              <NavLink
                to="/orders"
                activeClassName="selected"
                title={formatMessage({
                  id: 'navbar-tab-orders',
                  defaultMessage: 'Orders',
                })}
              >
                <FormattedMessage
                  id="navbar-tab-orders"
                  description="Orders option in navbar"
                  defaultMessage="Orders"
                />
              </NavLink>
            </li>
            <li className="navsmall">
              <NavLink
                to="/trades"
                activeClassName="selected"
                title={formatMessage({
                  id: 'navbar-tab-trades',
                  defaultMessage: 'Trades',
                })}
              >
                <FormattedMessage
                  id="navbar-tab-trades"
                  description="Trades option in navbar"
                  defaultMessage="Trades"
                />
              </NavLink>
            </li>

            <Authorised to="view" model={participants}>
              <li className="navsmall">
                <NavLink
                  to="/participants"
                  activeClassName="selected"
                  title={formatMessage({
                    id: 'navbar-tab-participants',
                    defaultMessage: 'Participants',
                  })}
                >
                  <FormattedMessage
                    id="navbar-tab-participants"
                    description="Participants option in navbar"
                    defaultMessage="Participants"
                  />
                </NavLink>
              </li>
            </Authorised>

            <Authorised to="view" model={consolidation}>
              <li className="navsmall">
                <NavLink
                  to="/consolidation"
                  title={formatMessage({
                    id: 'navbar-tab-consolidation',
                    defaultMessage: 'Consolidation',
                  })}
                  activeClassName="selected"
                >
                  <FormattedMessage
                    id="navbar-tab-consolidation"
                    description="Consolidation option in navbar"
                    defaultMessage="Consolidation"
                  />
                </NavLink>
              </li>
            </Authorised>
            <Authorised to="view" model={deliveries}>
              <li className="navsmall">
                <NavLink
                  to="/deliveries"
                  title={formatMessage({
                    id: 'navbar-tab-deliveries',
                    defaultMessage: 'Deliveries',
                  })}
                  activeClassName="selected"
                >
                  <FormattedMessage
                    id="navbar-tab-deliveries"
                    description="Deliveries option in navbar"
                    defaultMessage="Deliveries"
                  />
                </NavLink>
              </li>
            </Authorised>
            {isadmin && (
              <li className="navsmall">
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${domain}/signin`}
                  title={formatMessage({
                    id: 'navbar-tab-admin',
                    defaultMessage: 'Admin',
                  })}
                  className="admin"
                >
                  Admin
                </a>
              </li>
            )}
          </>
        )}
      </ul>
    </nav>
  );
};

const MainMenu = forwardRef(({ width }, ref) => {
  const navRef = useRef(null);
  const helpSectionRef = useRef(null);

  const [fullNavWidth, setFullNavWidth] = useState(null);
  const [smallMainNav, setSmallMainNav] = useState(false);

  useEffect(() => {
    // set full nav width - only if its greater in case browser loaded mobile first
    if (width > 0 && navRef.current?.clientWidth > fullNavWidth) {
      setFullNavWidth(navRef.current?.clientWidth);
    }
  }, [navRef.current, width]);

  useEffect(() => {
    const nav = navRef.current;
    const helpSection = helpSectionRef.current;

    // collapse to small nav if there's not enough space
    if (
      width - nav?.offsetLeft - fullNavWidth - 120 <
      helpSection?.clientWidth
    ) {
      setSmallMainNav(true);
    } else {
      setSmallMainNav(false);
    }
  }, [width, fullNavWidth]);

  const solutionDivisions = useStoreState(
    state => state.auth.solutionDivisions
  );

  const canCreateLiquidity = solutionDivisions.some(s => s.createliquidity);

  return (
    <div role="navigation" className="nbar navbar" ref={ref}>
      <TradeButtons />
      {width > 768 && (
        <div className="navigation" ref={navRef}>
          <MainNav
            smallMainNav={smallMainNav}
            canCreateLiquidity={canCreateLiquidity}
          />
          <SecondaryMenu
            smallMainNav={smallMainNav}
            canCreateLiquidity={canCreateLiquidity}
          />
        </div>
      )}
      <HelpSection ref={helpSectionRef} />
    </div>
  );
});

export default () => {
  const [containerRef, { width }] = useMeasure();
  return (
    <div className="sticky-nav" ref={containerRef}>
      {width <= 768 && <MobileMenu />}
      <MainMenu ref={containerRef} width={width} />
    </div>
  );
};
